import { FC, useState, ChangeEvent, useContext, KeyboardEvent, useEffect } from 'react';
import { User, UserCredential, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { CircularProgress, MobileStepper, useTheme } from '@mui/material';

import { EMPTY_STRING, ENTER, HOME_PATH, REGISTER_PATH } from '../../constants';
import { AuthContext } from '../../providers/AuthProvider';
import UserService from '../../services/userService';
import {
  ArrowImagesDiv, Button, ButtonStretched, CenterBox, CureytaBigImg, ErrorText, GoogleButton, GoogleIconImg, Heading2,
  Input, LinkPurple, LogoBigImg, Part1Div, Part2Div, RowDiv, StepIndex, Text, TextGray, TwoPartDiv,
  WhiteSpinnerSpan
} from '../../styles/styles';
import GoogleIcon from "../../imgs/google.svg";
import { KeyboardArrowRight } from '@mui/icons-material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { auth, googleAuthProvider } from '../../firebase';
import { emptyAccount } from '../../services/emptyObjs';
import { getSteps } from '../../services/helperService';
import Cureyta from "../../imgs/Cureyta-big.svg";
import Logo from "../../imgs/logo-big.svg";
import StepOne from "../../imgs/steps-1.svg";
import Arrow from "../../imgs/arrow.svg";
import DashedLine from "../../imgs/dashed-line.svg";
import { Timestamp } from 'firebase/firestore';

const Login: FC = () => {
  const { currentUser } = useContext(AuthContext);
  const [email, setEmail] = useState(EMPTY_STRING);
  const [password, setPassword] = useState(EMPTY_STRING);
  const [loginError, setLoginError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(EMPTY_STRING);
  const [googleIsLoading, setGoogleIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const steps = getSteps();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoginError(false);
    setEmail(event.target.value);
  }

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoginError(false);
    setPassword(event.target.value);
  }

  const handleSignUpClick = () => {
    setLoginError(false);
    window.location.assign(REGISTER_PATH);
  }

  const handleForgotPasswordClick = async () => {
    setLoginError(false);
    window.scrollTo(0, 0);
  };

  const existingUser = async (authUser: User) : Promise<boolean> => {
    if (authUser) {
      const account: Account | null = await UserService.getAccountById(authUser.uid);
      if (account) {
        return true;
      }
    }
    return false;
  }

  const handleEnterPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ENTER) {
      handleLoginClick()
    }
  };

  const handleSignInGoogleClick = async () => {
    setGoogleIsLoading(true);
    try {
      const userCredential: UserCredential = await signInWithPopup(auth, googleAuthProvider);
      if (!userCredential) {
        return;
      }
      const authUser: User = userCredential.user;
      const userExists = await existingUser(authUser);
      if (!userExists) {
        const newAccount: Account = {
          ...emptyAccount,
          id: authUser.uid,
          email: authUser.email,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        await UserService.addAccount(newAccount);
      }
    } catch (error) {
      console.error(error);
      setLoginError(true);
      setErrorMessage("Cannot log you in with that account. Please try with another.");
    }
    setGoogleIsLoading(false);
  };

  const handleLoginClick = async () => {
    if (email === EMPTY_STRING || password === EMPTY_STRING) {
      setLoginError(true);
      setErrorMessage("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      const userExists: boolean = await existingUser(userCredential.user);
      if (!userExists) {
        setLoginError(true);
        setErrorMessage("Cannot find account with that email and password. Please make sure your email and password are correct.");
        return;
      }
      window.location.assign(HOME_PATH);
    } catch (error) {
      setLoginError(true);
      setErrorMessage("Cannot find account with that email and password. Please make sure your email and password are correct.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser) {
      window.location.assign(HOME_PATH);
    }
  }, [currentUser]);

  return (
    <TwoPartDiv>
      <Part1Div>
        <CenterBox style={{ marginTop: "100px" }}>
          <LogoBigImg src={Logo} />
          <CureytaBigImg src={Cureyta} />
          <TextGray style={{ marginBottom: "36px", fontSize: "16px", textAlign: "center" }}>Turn clutter into memories</TextGray>
          {!googleIsLoading && <>
            <Input type='email' placeholder='Email' value={email} onChange={handleEmailChange} required />
            { loginError && <ErrorText style={{ marginRight: "auto", marginLeft: "auto" }}>{errorMessage}</ErrorText>}
            <Input type='password' placeholder='Password' value={password} onChange={handlePasswordChange} onKeyDown={handleEnterPress} required style={{ marginTop: "24px" }} />
            <ButtonStretched onClick={handleLoginClick} style={{ marginTop: "44px" }}>
              {loading &&
                <WhiteSpinnerSpan>
                  <CircularProgress style={{ width: "30px", height: "30px", color: "#fff" }} />
                </WhiteSpinnerSpan>
              }
              {!loading && <>Sign In</>}
            </ButtonStretched>
            <Text style={{ fontSize: "15px", textAlign: "center", marginTop: "20px", marginBottom: "20px" }}>or</Text></>
          }
          <GoogleButton onClick={handleSignInGoogleClick} style={{ marginBottom: "30px" }}>
            <GoogleIconImg src={GoogleIcon} />
            {googleIsLoading ?
              <span style={{ width: "180px", textAlign: "center" }}>
                <CircularProgress style={{ width: "30px", height: "30px" }} />
              </span>
              :
              <>Continue with Google</>
            }
          </GoogleButton>
          {/* <LinkBlack onClick={handleForgotPasswordClick}>Forgot password</LinkBlack> */}
          <Text style={{ marginTop: "20px" }}>Don't have an account? <LinkPurple onClick={handleSignUpClick}>Sign Up</LinkPurple></Text>
        </CenterBox>
      </Part1Div>
      <Part2Div>
        <RowDiv style={{ width: "auto", gap: "8px" }}>
          <StepIndex>{steps[activeStep].index}</StepIndex>
          <Heading2>{steps[activeStep].title}</Heading2>
        </RowDiv>
        <TextGray className='center' style={{ marginBottom: "20px", fontSize: "16px", textAlign: "center", margin: "12px auto 24px auto", width: "100%", maxWidth: "500px" }}>
          {steps[activeStep].content}
        </TextGray>
        <MobileStepper variant="dots" steps={4} position="static" activeStep={activeStep}
          sx={{
            backgroundColor: "transparent",
            width: "300px",
            '& .MuiMobileStepper-dotActive': {
              backgroundColor: "#A20B91",
            }
          }}
          nextButton={
            <Button onClick={handleNext} disabled={activeStep === 3}>
              {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button onClick={handleBack} disabled={activeStep === 0}>
              {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </Button>
          }
        />
        <ArrowImagesDiv>
          <img src={StepOne} className="step-image" />
          <img src={Arrow} className="arrow-image" />
          <img src={DashedLine} className="dashed-line" />
        </ArrowImagesDiv>
      </Part2Div>
    </TwoPartDiv>
  );
};

export default Login;
